import React, { useState, useRef, useEffect } from 'react';

/**
 * Shared Trovex inline sign-in / sign-up screen — generalised from
 * apps/voip/src/components/Login.jsx (the gold standard). Primary path is
 * a 6-digit email code (no inbox round-trip, no id.trovex.com hop);
 * "Sign in with Trovex ID" remains as a fallback.
 *
 * Requires the consuming app's CSS to provide .gradient-mesh, .card-soft,
 * .recording-orb-ring and .pb-safe (the voip visual language).
 *
 * Props:
 *   appName                              — accent word in the title ("Phone", "Users")
 *   tagline                              — optional line under the title (email stage)
 *   icon                                 — optional ReactNode replacing the default orb glyph
 *   onTrovexIdLogin()                    — bounce to id.trovex.com (fallback)
 *   onRequestCode(email)  -> Promise     — sends the 6-digit code
 *   onVerifyCode(email, code) -> Promise — verifies + signs in
 *   allowedDomains                       — for client-side validation hinting
 */
export default function Login({
  appName = 'ID',
  tagline,
  icon,
  onTrovexIdLogin,
  onRequestCode,
  onVerifyCode,
  allowedDomains = ['@trovex.com', '@focuswashrooms.com'],
}) {
  const [stage, setStage] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (stage === 'code' && codeRef.current) codeRef.current.focus();
  }, [stage]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const domainOk = (addr) => allowedDomains.some((d) => addr.endsWith(d));

  const sendCode = async (e) => {
    e?.preventDefault?.();
    setError('');
    const addr = email.trim().toLowerCase();
    if (!domainOk(addr)) {
      setError('Enter your @trovex.com email address.');
      return;
    }
    setBusy(true);
    try {
      await onRequestCode(addr);
      setStage('code');
      setResendIn(30);
    } catch (err) {
      setError(err?.details?.message || err?.message || 'Could not send your code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    try {
      await onVerifyCode(email.trim().toLowerCase(), code.trim());
      // Success — the auth listener swaps this screen for the app (or the
      // access-denied screen). Keep the button busy meanwhile.
    } catch (err) {
      setError(err?.details?.message || err?.message || "That code didn't work. Please try again.");
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || busy) return;
    setError('');
    setBusy(true);
    try {
      await onRequestCode(email.trim().toLowerCase());
      setResendIn(30);
      setCode('');
      if (codeRef.current) codeRef.current.focus();
    } catch (err) {
      setError(err?.details?.message || err?.message || 'Could not resend your code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] gradient-mesh overflow-hidden">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-gradient-to-br from-teal-300/30 to-emerald-300/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-emerald-300/25 to-teal-300/15 blur-3xl" />
      </div>

      <div className="relative h-full w-full flex flex-col items-center justify-between px-6 py-10">
        <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
          <div className="relative w-24 h-24 flex items-center justify-center mb-8">
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-teal-400/40 to-emerald-400/30 recording-orb-ring" />
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-teal-400/30 to-emerald-400/20 recording-orb-ring" style={{ animationDelay: '0.65s' }} />
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-teal-400/20 to-emerald-400/10 recording-orb-ring" style={{ animationDelay: '1.3s' }} />
            <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 shadow-xl shadow-teal-500/40 flex items-center justify-center">
              {icon || (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
              )}
            </div>
          </div>

          <h1 className="text-3xl font-semibold text-gray-800 dark:text-gray-100 text-center tracking-tight">
            Trovex{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500 dark:from-teal-400 dark:to-emerald-300">{appName}</span>
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center leading-relaxed">
            {stage === 'email'
              ? (tagline || 'Enter your Trovex email and we’ll send you a 6-digit code to sign in.')
              : `Enter the 6-digit code we sent to ${email}.`}
          </p>

          <div className="w-full mt-8 card-soft border border-gray-200/60 dark:border-white/10 bg-white/80 dark:bg-[#1e1e1e]/85 backdrop-blur-sm p-6">
            {stage === 'email' ? (
              <form onSubmit={sendCode} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="firstname.lastname@trovex.com"
                  autoComplete="email"
                  inputMode="email"
                  autoFocus
                  disabled={busy}
                  className="w-full px-3 py-3 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 disabled:opacity-50"
                />
                {error && <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>}
                <button
                  type="submit"
                  disabled={busy || !email.trim()}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 shadow-lg shadow-teal-500/25 transition-all active:scale-[0.99] disabled:opacity-50"
                >
                  {busy ? 'Sending…' : 'Send my code'}
                </button>
              </form>
            ) : (
              <form onSubmit={verify} className="space-y-3">
                <input
                  ref={codeRef}
                  type="text"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  disabled={busy}
                  className="w-full px-3 py-3 text-center text-2xl font-semibold tracking-[0.5em] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 disabled:opacity-50"
                />
                {error && <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>}
                <button
                  type="submit"
                  disabled={busy || code.trim().length !== 6}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 shadow-lg shadow-teal-500/25 transition-all active:scale-[0.99] disabled:opacity-50"
                >
                  {busy ? 'Signing in…' : 'Verify & sign in'}
                </button>
                <div className="flex items-center justify-between text-[11px] pt-1">
                  <button type="button" onClick={() => { setStage('email'); setCode(''); setError(''); }} className="text-gray-500 dark:text-gray-400 hover:underline">
                    Use a different email
                  </button>
                  <button type="button" onClick={resend} disabled={resendIn > 0 || busy} className="text-teal-700 dark:text-teal-300 hover:underline disabled:opacity-50 disabled:no-underline">
                    {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            )}

            <div className="flex items-center gap-3 my-5">
              <span className="flex-1 h-px bg-gray-200/70 dark:bg-white/10" />
              <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">or</span>
              <span className="flex-1 h-px bg-gray-200/70 dark:bg-white/10" />
            </div>
            <button
              type="button"
              onClick={onTrovexIdLogin}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
              Sign in with Trovex ID
            </button>

            <div className="flex items-center gap-2 mt-5 text-[11px] text-gray-400 dark:text-gray-500">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
              <span>@trovex.com accounts only — your Trovex ID works across every Trovex app.</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 pb-safe">
          <img src="/trovex-logo.svg" alt="Trovex" className="h-5 opacity-70 dark:opacity-60" />
          <p className="text-[11px] text-gray-400 dark:text-gray-500 tracking-wide">Developed by Group AI and Systems</p>
        </div>
      </div>
    </div>
  );
}
